import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { format } from "date-fns";
import api from "../utils/api";
import LicenseForm from "../components/LicenseForm";
import LicenseDetails from "../components/LicenseDetails";

const Licenses = () => {
  const apiRef = useGridApiRef();
  const [licenses, setLicenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    status: "",
    categoryGroup: "",
  });
  const [displayCurrency, setDisplayCurrency] = useState("GBP"); // Currency to display amounts in
  const [includeExpired, setIncludeExpired] = useState(false); // Toggle: include expired records (off by default)
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 50,
    total: 0,
  });

  // Dialog states
  const [openForm, setOpenForm] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [editingLicense, setEditingLicense] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [updatingGbp, setUpdatingGbp] = useState(false);
  const [selectedLicenses, setSelectedLicenses] = useState([]);
  const [openBulkAssign, setOpenBulkAssign] = useState(false);
  const [bulkCategoryGroup, setBulkCategoryGroup] = useState("");
  const [existingCategoryGroups, setExistingCategoryGroups] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      apiRef.current?.resize?.();
    };
    window.addEventListener("resize", handleResize);
    const t = window.setTimeout(handleResize, 50);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.clearTimeout(t);
    };
  }, [apiRef, licenses, loading]);

  const fetchCategories = async () => {
    try {
      const response = await api.get("/api/categories");
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchCategoryGroups = async () => {
    try {
      const response = await api.get("/api/licenses/category-groups");
      setExistingCategoryGroups(response.data.categoryGroups || []);
    } catch (error) {
      console.error("Failed to fetch category groups:", error);
    }
  };

  const fetchLicenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page + 1,
        limit: pagination.pageSize,
        ...filters,
        ...(includeExpired ? {} : { excludeExpired: true }),
      };

      const response = await api.get("/api/licenses", { params });
      setLicenses(response.data.licenses);
      setPagination((prev) => ({
        ...prev,
        total: response.data.pagination.total,
      }));
    } catch (error) {
      console.error("Failed to fetch licenses:", error);
      setError("Failed to load licenses");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize, includeExpired]);

  useEffect(() => {
    fetchCategories();
    fetchCategoryGroups();
    fetchLicenses();
  }, [fetchLicenses]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleDelete = useCallback(
    async (id) => {
      if (window.confirm("Are you sure you want to delete this license?")) {
        try {
          await api.delete(`/api/licenses/${id}`);
          fetchLicenses();
        } catch (error) {
          console.error("Failed to delete license:", error);
          setError("Failed to delete license");
        }
      }
    },
    [fetchLicenses],
  );

  const handleView = (license) => {
    setSelectedLicense(license);
    setOpenDetails(true);
  };

  const handleEdit = (license) => {
    setEditingLicense(license);
    setOpenForm(true);
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setEditingLicense(null);
  };

  const handleFormSubmit = async (licenseData) => {
    try {
      console.log("Submitting license data:", licenseData);
      console.log("API base URL:", api.defaults.baseURL);

      if (editingLicense) {
        console.log("Updating existing license:", editingLicense.id);
        await api.put(`/api/licenses/${editingLicense.id}`, licenseData);
      } else {
        console.log("Creating new license");
        await api.post("/api/licenses", licenseData);
      }
      console.log("License saved successfully");
      console.log("Closing form...");
      handleFormClose();
      console.log("Refreshing licenses...");
      fetchLicenses();
    } catch (error) {
      console.error("Failed to save license:", error);

      // Handle validation errors
      if (error.response?.data?.errors) {
        const validationErrors = {};
        error.response.data.errors.forEach((err) => {
          validationErrors[err.field] = err.message;
        });

        // Show validation errors in a more user-friendly way
        const errorMessages = error.response.data.errors
          .map((err) => err.message)
          .join("\n");
        setError(`Validation failed:\n${errorMessages}`);
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError("Failed to save license. Please try again.");
      }

      throw error;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "expired":
        return "error";
      case "cancelled":
        return "default";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  // Function to format amount in selected currency
  const formatAmount = useCallback(
    (license, amountType = "billing") => {
      if (displayCurrency === "GBP") {
        // Show GBP amounts (stored or calculated)
        if (license.currency === "GBP") {
          return {
            amount:
              license[
                amountType === "billing" ? "billing_amount" : "renewal_amount"
              ],
            net: license[
              amountType === "billing" ? "net_amount" : "renewal_net_amount"
            ],
            vat: license[
              amountType === "billing" ? "vat_amount" : "renewal_vat_amount"
            ],
            currency: "GBP",
          };
        } else {
          // Show stored GBP equivalent with fallback to original amount
          const gbpAmount =
            license[
              amountType === "billing"
                ? "billing_amount_gbp"
                : "renewal_amount_gbp"
            ];
          const gbpNet =
            license[
              amountType === "billing"
                ? "net_amount_gbp"
                : "renewal_net_amount_gbp"
            ];
          const gbpVat =
            license[
              amountType === "billing"
                ? "vat_amount_gbp"
                : "renewal_vat_amount_gbp"
            ];

          return {
            amount:
              gbpAmount !== null && gbpAmount !== undefined
                ? gbpAmount
                : license[
                    amountType === "billing"
                      ? "billing_amount"
                      : "renewal_amount"
                  ],
            net:
              gbpNet !== null && gbpNet !== undefined
                ? gbpNet
                : license[
                    amountType === "billing"
                      ? "net_amount"
                      : "renewal_net_amount"
                  ],
            vat:
              gbpVat !== null && gbpVat !== undefined
                ? gbpVat
                : license[
                    amountType === "billing"
                      ? "vat_amount"
                      : "renewal_vat_amount"
                  ],
            currency: "GBP",
          };
        }
      } else {
        // Show original currency amounts
        return {
          amount:
            license[
              amountType === "billing" ? "billing_amount" : "renewal_amount"
            ],
          net: license[
            amountType === "billing" ? "net_amount" : "renewal_net_amount"
          ],
          vat: license[
            amountType === "billing" ? "vat_amount" : "renewal_vat_amount"
          ],
          currency: license.currency,
        };
      }
    },
    [displayCurrency],
  );

  const updateGbpEquivalents = async () => {
    try {
      setUpdatingGbp(true);
      const response = await api.post("/api/licenses/update-gbp-equivalents");
      console.log("GBP equivalents update result:", response.data);

      // Refresh the licenses list to show updated amounts
      await fetchLicenses();

      // Show success message
      alert(
        `GBP equivalents updated successfully! Updated ${response.data.updatedCount} licenses.`,
      );
    } catch (error) {
      console.error("Failed to update GBP equivalents:", error);
      alert("Failed to update GBP equivalents. Please try again.");
    } finally {
      setUpdatingGbp(false);
    }
  };

  const exportToCSV = async () => {
    try {
      setExporting(true);

      // Fetch all licenses with current filters (no pagination)
      const params = {
        ...filters,
        limit: 10000, // Large limit to get all results
        ...(includeExpired ? {} : { excludeExpired: true }),
      };

      const response = await api.get("/api/licenses", { params });
      const allLicenses = response.data?.licenses || [];
      const totalCount = response.data?.pagination?.total ?? allLicenses.length;

      if (allLicenses.length === 0) {
        setError("No licenses found to export");
        return;
      }

      if (allLicenses.length < totalCount) {
        console.warn(
          `Exporting ${allLicenses.length} of ${totalCount} licenses due to limit`,
        );
      }

      // Define CSV headers
      const headers = [
        "Name",
        "Supplier Name",
        "Category",
        "License Key",
        "License Type",
        "Billing Frequency",
        "Billing Amount",
        "Currency",
        "Start Date",
        "Expiry Date",
        "Status",
        "User Allocation",
        "Auto Renewal",
        "Renewal Amount",
        "Notes",
        "Created By",
      ];

      // Convert data to CSV format
      const csvData = allLicenses.map((license) => [
        license.name || "",
        license.vendor || "",
        license.category_name || "Uncategorized",
        license.license_key || "",
        license.license_type || "",
        license.billing_frequency || "",
        license.billing_amount || "",
        license.currency || "",
        license.start_date
          ? format(new Date(license.start_date), "dd/MM/yyyy")
          : "",
        license.expiry_date
          ? format(new Date(license.expiry_date), "dd/MM/yyyy")
          : "",
        license.status || "",
        license.user_allocation || "",
        license.auto_renewal ? "Yes" : "No",
        license.renewal_amount || "",
        license.notes || "",
        license.created_by_name || "",
      ]);

      // Combine headers and data
      const csvContent = [headers, ...csvData]
        .map((row) => row.map((field) => `"${field}"`).join(","))
        .join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `licenses_export_${format(new Date(), "yyyy-MM-dd_HH-mm")}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show success message
      setError(""); // Clear any previous errors
      // You could add a success state here if needed
    } catch (error) {
      console.error("Export failed:", error);
      setError("Failed to export licenses");
    } finally {
      setExporting(false);
    }
  };

  // Group licenses by category_group for visual bands
  const groupedLicenses = useMemo(() => {
    const groups = {};
    licenses.forEach((license) => {
      // Skip group headers
      if (license.__isGroupHeader) return;
      const group = license.category_group || "Uncategorized";
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(license);
    });
    return groups;
  }, [licenses]);

  // Flatten grouped licenses with group headers for display
  const licensesWithGroups = useMemo(() => {
    const result = [];
    const groupOrder = [
      "Employee Benefits",
      "Licensing",
      "Subscriptions",
      "Uncategorized",
    ];

    // Add groups in order
    groupOrder.forEach((groupName) => {
      if (groupedLicenses[groupName] && groupedLicenses[groupName].length > 0) {
        result.push({
          __isGroupHeader: true,
          __groupName: groupName,
          id: `group-${groupName}`,
        });
        result.push(...groupedLicenses[groupName]);
      }
    });

    // Add any other groups not in the predefined order
    Object.keys(groupedLicenses).forEach((groupName) => {
      if (
        !groupOrder.includes(groupName) &&
        groupedLicenses[groupName].length > 0
      ) {
        result.push({
          __isGroupHeader: true,
          __groupName: groupName,
          id: `group-${groupName}`,
        });
        result.push(...groupedLicenses[groupName]);
      }
    });

    return result;
  }, [groupedLicenses]);

  const handleBulkAssign = async () => {
    if (selectedLicenses.length === 0) {
      alert("Please select at least one license");
      return;
    }

    try {
      await api.post("/api/licenses/bulk-assign-category-group", {
        licenseIds: selectedLicenses,
        categoryGroup: bulkCategoryGroup || null,
      });
      setOpenBulkAssign(false);
      setSelectedLicenses([]);
      setBulkCategoryGroup("");
      fetchLicenses();
      fetchCategoryGroups();
      alert(`Category group assigned to ${selectedLicenses.length} license(s)`);
    } catch (error) {
      console.error("Failed to assign category group:", error);
      alert("Failed to assign category group");
    }
  };

  const columns = useMemo(() => {
    return [
      {
        field: "id",
        headerName: "",
        width: 50,
        sortable: false,
        renderCell: (params) => {
          if (params.row.__isGroupHeader) return null;
          return (
            <input
              type="checkbox"
              checked={selectedLicenses.includes(params.row.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedLicenses([...selectedLicenses, params.row.id]);
                } else {
                  setSelectedLicenses(
                    selectedLicenses.filter((id) => id !== params.row.id),
                  );
                }
              }}
            />
          );
        },
      },
      {
        field: "name",
        headerName: "Name",
        flex: 2.5,
        minWidth: 200,
        renderCell: (params) => {
          if (params.row.__isGroupHeader) {
            return (
              <Typography variant="h6" fontWeight={600} sx={{ py: 1 }}>
                {params.row.__groupName}
              </Typography>
            );
          }
          return params.value;
        },
      },
      {
        field: "vendor",
        headerName: "Supplier Name",
        flex: 1.4,
        minWidth: 140,
        renderCell: (params) => {
          if (params.row.__isGroupHeader) return null;
          return params.value;
        },
      },
      {
        field: "category_name",
        headerName: "Category",
        flex: 1.1,
        minWidth: 130,
        renderCell: (params) => {
          if (params.row.__isGroupHeader) return null;
          return (
            <Chip
              label={params.value || "Uncategorized"}
              size="small"
              sx={{
                backgroundColor: params.row.category_color || "#e0e0e0",
                color: "white",
              }}
            />
          );
        },
      },
      {
        field: "billing_amount",
        headerName: "Cost",
        flex: 1.1,
        minWidth: 150,
        renderCell: (params) => {
          if (params.row.__isGroupHeader) return null;
          const formattedAmount = formatAmount(params.row, "billing");
          const currencySymbols = {
            GBP: "£",
            USD: "$",
            EUR: "€",
            CAD: "C$",
          };

          // Check if we're showing original amount instead of GBP conversion
          const isShowingOriginalInsteadOfGbp =
            displayCurrency === "GBP" &&
            params.row.currency !== "GBP" &&
            params.row.billing_amount_gbp === null;

          // Show VAT breakdown if includes_vat is true (handles both boolean true and numeric 1 from MySQL)
          // Only display non-zero values to avoid showing "0" when there's no split
          const includesVat =
            params.row.includes_vat === true ||
            params.row.includes_vat === 1 ||
            params.row.includes_vat === "1";
          const netValue = formattedAmount.net;
          const vatValue = formattedAmount.vat;
          const netNum =
            typeof netValue === "string" ? parseFloat(netValue) : netValue;
          const vatNum =
            typeof vatValue === "string" ? parseFloat(vatValue) : vatValue;
          const hasNet = netNum != null && !isNaN(netNum) && netNum > 0;
          const hasVat = vatNum != null && !isNaN(vatNum) && vatNum > 0;
          // Show breakdown if includes_vat is true AND at least one of net or vat has a meaningful value
          const shouldShowVatBreakdown = includesVat && (hasNet || hasVat);

          return (
            <Box>
              <Typography variant="body2">
                {currencySymbols[formattedAmount.currency]}
                {formattedAmount.amount}
                {isShowingOriginalInsteadOfGbp && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="warning.main"
                    sx={{ ml: 1 }}
                  >
                    (Original {params.row.currency})
                  </Typography>
                )}
              </Typography>
              {shouldShowVatBreakdown && (
                <Typography variant="caption" color="textSecondary">
                  {hasNet && (
                    <>
                      Net: {currencySymbols[formattedAmount.currency]}
                      {netValue}
                    </>
                  )}
                  {hasNet && hasVat && " | "}
                  {hasVat && (
                    <>
                      VAT: {currencySymbols[formattedAmount.currency]}
                      {vatValue}
                    </>
                  )}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "billing_frequency",
        headerName: "Frequency",
        flex: 0.7,
        minWidth: 110,
        renderCell: (params) => {
          if (params.row.__isGroupHeader) return null;
          return params.value
            ? params.value.charAt(0).toUpperCase() + params.value.slice(1)
            : "";
        },
      },
      {
        field: "user_allocation",
        headerName: "Users",
        flex: 0.5,
        minWidth: 80,
        renderCell: (params) => {
          if (params.row.__isGroupHeader) return null;
          return (
            <Chip
              label={params.value || 1}
              size="small"
              variant="outlined"
              color="primary"
            />
          );
        },
      },
      {
        field: "expiry_date",
        headerName: "Expiry Date",
        flex: 0.9,
        minWidth: 120,
        renderCell: (params) => {
          if (params.row.__isGroupHeader) return null;
          return params.value
            ? format(new Date(params.value), "dd MMM yyyy")
            : "";
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.7,
        minWidth: 100,
        renderCell: (params) => {
          if (params.row.__isGroupHeader) return null;
          return (
            <Chip
              label={params.value}
              color={getStatusColor(params.value)}
              size="small"
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.8,
        minWidth: 130,
        sortable: false,
        renderCell: (params) => {
          if (params.row.__isGroupHeader) return null;
          return (
            <Box>
              <IconButton
                size="small"
                onClick={() => handleView(params.row)}
                color="primary"
              >
                <ViewIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleEdit(params.row)}
                color="primary"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDelete(params.row.id)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        },
      },
    ];
  }, [displayCurrency, selectedLicenses, formatAmount, handleDelete]);

  return (
    <Box width="100%">
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportToCSV}
            disabled={exporting}
          >
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={updateGbpEquivalents}
            disabled={updatingGbp}
          >
            {updatingGbp ? "Updating..." : "Update GBP Equivalents"}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenForm(true)}
          >
            Add License
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters - one row, scales to device/page size */}
      <Card sx={{ mb: 3, width: "100%" }}>
        <CardContent>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="flex-start"
            sx={{
              minWidth: 0,
              gap: { xs: 1, sm: 2 },
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <Box flex="1 1 0" minWidth={0} sx={{ minWidth: 80 }}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder="Search by name, supplier, or license key"
              />
            </Box>
            <Box flex="1 1 0" minWidth={0} sx={{ minWidth: 80 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Category Group</InputLabel>
                <Select
                  value={filters.categoryGroup}
                  label="Category Group"
                  onChange={(e) =>
                    handleFilterChange("categoryGroup", e.target.value)
                  }
                >
                  <MenuItem value="">All Groups</MenuItem>
                  <MenuItem value="Employee Benefits">
                    Employee Benefits
                  </MenuItem>
                  <MenuItem value="Licensing">Licensing</MenuItem>
                  <MenuItem value="Subscriptions">Subscriptions</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box flex="1 1 0" minWidth={0} sx={{ minWidth: 80 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  label="Category"
                  onChange={(e) =>
                    handleFilterChange("category", e.target.value)
                  }
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories
                    .filter(
                      (cat) =>
                        !filters.categoryGroup ||
                        cat.category_group === filters.categoryGroup,
                    )
                    .map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
            <Box flex="1 1 0" minWidth={0} sx={{ minWidth: 80 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box flex="1 1 0" minWidth={0} sx={{ minWidth: 80 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Display Currency</InputLabel>
                <Select
                  value={displayCurrency}
                  label="Display Currency"
                  onChange={(e) => setDisplayCurrency(e.target.value)}
                >
                  <MenuItem value="GBP">GBP (£)</MenuItem>
                  <MenuItem value="USD">USD ($)</MenuItem>
                  <MenuItem value="EUR">EUR (€)</MenuItem>
                  <MenuItem value="CAD">CAD ($)</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box
              flex="0 0 auto"
              display="flex"
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={includeExpired}
                    onChange={(e) => {
                      setIncludeExpired(e.target.checked);
                      setPagination((prev) => ({ ...prev, page: 0 }));
                    }}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Include expired
                  </Typography>
                }
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Bulk Assign Button */}
      {selectedLicenses.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body1">
                {selectedLicenses.length} license(s) selected
              </Typography>
              <Button
                variant="contained"
                onClick={() => setOpenBulkAssign(true)}
              >
                Bulk Assign Category Group
              </Button>
              <Button
                variant="outlined"
                onClick={() => setSelectedLicenses([])}
              >
                Clear Selection
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Data Grid - expand to fit all rows (page scrolls, not grid) */}
      <Card sx={{ width: "100%" }}>
        <CardContent sx={{ width: "100%", p: 0, "&:last-child": { pb: 0 } }}>
          <Box width="100%">
            <DataGrid
              apiRef={apiRef}
              rows={licensesWithGroups}
              columns={columns}
              loading={loading}
              autoHeight
              getRowHeight={() => "auto"}
              pagination
              paginationMode="server"
              rowCount={pagination.total}
              paginationModel={{
                page: pagination.page,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={(model) => {
                setPagination((prev) => ({
                  ...prev,
                  page: model.page,
                  pageSize: model.pageSize,
                  ...(model.pageSize !== prev.pageSize ? { page: 0 } : {}),
                }));
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              disableRowSelectionOnClick
              disableColumnMenu
              getRowId={(row) =>
                row.__isGroupHeader
                  ? `group-${row.__groupName}`
                  : String(row.id)
              }
              getRowClassName={(params) => {
                if (params.row.__isGroupHeader) {
                  return "category-group-header";
                }
                return "";
              }}
              sx={{
                width: "100%",
                border: 0,
                "& .MuiDataGrid-main": { width: "100%" },
                "& .MuiDataGrid-virtualScroller": { width: "100%" },
                "& .MuiDataGrid-cell": {
                  py: 1,
                  alignItems: "center",
                  display: "flex",
                },
                "& .category-group-header": {
                  backgroundColor: "#f5f5f5",
                  fontWeight: 600,
                  borderBottom: "2px solid #1976d2",
                },
                "& .category-group-header .MuiDataGrid-cell": {
                  py: 1.5,
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* License Form Dialog */}
      <Dialog open={openForm} onClose={handleFormClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLicense ? "Edit License" : "Add New License"}
        </DialogTitle>
        <DialogContent>
          <LicenseForm
            license={editingLicense}
            categories={categories}
            existingCategoryGroups={existingCategoryGroups}
            onSubmit={handleFormSubmit}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      {/* License Details Dialog */}
      <Dialog
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {selectedLicense && (
            <LicenseDetails
              license={selectedLicense}
              onEdit={() => {
                setOpenDetails(false);
                handleEdit(selectedLicense);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog
        open={openBulkAssign}
        onClose={() => {
          setOpenBulkAssign(false);
          setBulkCategoryGroup("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Assign Category Group</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Assigning to {selectedLicenses.length} license(s)
            </Typography>
            <Autocomplete
              freeSolo
              options={existingCategoryGroups}
              value={bulkCategoryGroup || null}
              onChange={(event, newValue) => {
                setBulkCategoryGroup(newValue || "");
              }}
              onInputChange={(event, newInputValue) => {
                if (event && event.type === "change") {
                  setBulkCategoryGroup(newInputValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category Group"
                  placeholder="Select or type to create new"
                  helperText="High-level classification (e.g., Employee Benefits, Licensing, Subscriptions)"
                  fullWidth
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenBulkAssign(false);
              setBulkCategoryGroup("");
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleBulkAssign} variant="contained">
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Licenses;
